import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Activity, X } from 'lucide-react';
import { useJobs } from './JobProvider';
import { JobProgressCard } from './JobProgressCard';
import { destinationForJob } from './jobDestination';

const TERMINAL_JOB_STATUSES = new Set(['completed', 'partial_completed', 'failed', 'cancelled']);

export const ApplicationJobIndicator = ({ applicationId, onOpen }) => {
  const { jobs } = useJobs();
  const active = jobs.filter((job) => job.application_id === applicationId && ['queued', 'running', 'cancelling'].includes(job.status));
  return (
    <button type="button" className="application-job-indicator" onClick={onOpen} aria-label={`Open jobs. ${active.length} active`}>
      <Activity aria-hidden="true" />
      <span><strong>{active.length}</strong> active {active.length === 1 ? 'job' : 'jobs'}</span>
    </button>
  );
};

export const ApplicationJobCenter = ({ applicationId, applicationName, open, onClose, onNavigate }) => {
  const { jobs, itemsById, loading, error, cancel, retry } = useJobs();
  const closeRef = useRef(null);
  const previousFocus = useRef(null);
  const storageKey = `testsuite_hidden_jobs_${applicationId}`;
  const [hiddenIds, setHiddenIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch (_err) { return []; }
  });
  useEffect(() => {
    try { setHiddenIds(JSON.parse(localStorage.getItem(storageKey) || '[]')); } catch (_err) { setHiddenIds([]); }
  }, [storageKey]);
  const hideJobs = (jobIds) => {
    const next = [...new Set([...hiddenIds, ...jobIds])];
    localStorage.setItem(storageKey, JSON.stringify(next));
    setHiddenIds(next);
  };
  const visibleJobs = useMemo(() => jobs.filter((job) => job.application_id === applicationId && !hiddenIds.includes(job.id)), [applicationId, hiddenIds, jobs]);
  const removableJobs = visibleJobs.filter((job) => TERMINAL_JOB_STATUSES.has(job.status));
  const openJob = (job) => {
    const destination = destinationForJob(job, itemsById[job.id]);
    if (!destination) return;
    onClose();
    onNavigate?.(destination.href);
  };

  useEffect(() => {
    if (!open) return undefined;
    previousFocus.current = document.activeElement;
    closeRef.current?.focus();
    const handleKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      previousFocus.current?.focus?.();
    };
  }, [onClose, open]);

  if (!open) return null;
  return (
    <div className="job-center-layer">
      <button type="button" className="job-center-scrim" aria-label="Dismiss job center" onClick={onClose} />
      <aside className="job-center-panel" role="dialog" aria-modal="true" aria-labelledby="job-center-title">
        <header>
          <div><small>{applicationName}</small><h2 id="job-center-title">Background jobs</h2></div>
          <button ref={closeRef} type="button" className="job-center-close" onClick={onClose} aria-label="Close job center"><X aria-hidden="true" /></button>
        </header>
        <p className="job-center-intro">Keep working while longer operations finish. Progress is restored after a refresh.</p>
        {removableJobs.length > 0 && <div className="job-center-toolbar"><button type="button" onClick={() => hideJobs(removableJobs.map((job) => job.id))}>Clear finished ({removableJobs.length})</button></div>}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {visibleJobs.filter((job) => ['queued', 'running', 'cancelling'].includes(job.status)).length} jobs active.
        </div>
        <div className="job-center-list">
          {loading && visibleJobs.length === 0 && <p className="job-center-empty">Restoring jobs…</p>}
          {error && <p className="job-center-error">{error}</p>}
          {!loading && visibleJobs.length === 0 && <p className="job-center-empty"><Activity aria-hidden="true" />No jobs yet. New background work will appear here.</p>}
          {visibleJobs.map((job) => <JobProgressCard key={`${job.adapter}-${job.id}`} job={job} items={itemsById[job.id]} onCancel={cancel} onRetry={retry} onOpen={job.module && (job.source_id || job.pdf_id) ? openJob : undefined} onRemove={TERMINAL_JOB_STATUSES.has(job.status) ? () => hideJobs([job.id]) : undefined} />)}
        </div>
      </aside>
    </div>
  );
};
